/**
 * Yarn Recipe Master
 * ─────────────────────────────────────────────────────────────────────────────
 * Define cotton recipes per yarn count + end use + process. Used by the
 * Auto-Assignment Engine (Phase 2B) to select bales matching these targets.
 *
 * Lifecycle: DRAFT → SUBMITTED → ACTIVE | REJECTED → SUPERSEDED → ARCHIVED
 */
import { useState } from 'react'
import {
  App, Alert, Button, Card, Col, Divider, Form, Input, InputNumber,
  Modal, Popconfirm, Row, Select, Space, Table, Tag, Tabs, Typography,
} from 'antd'
import {
  PlusOutlined, EditOutlined, SendOutlined, CheckOutlined,
  CloseOutlined, InboxOutlined, FileTextOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { recipeApi } from '@/features/recipe/api'
import type { YarnRecipe } from '@/features/recipe/api'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'
import FormDrawer from '@/shared/components/FormDrawer'
import { usePermission } from '@/shared/hooks/usePermission'
import dayjs from 'dayjs'

const { Text, Title } = Typography

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'default', SUBMITTED: 'blue', APPROVED: 'green', ACTIVE: 'green',
  SUPERSEDED: 'orange', ARCHIVED: 'red',
}

const END_USES = ['KNITTING', 'WEAVING', 'SEWING', 'INDUSTRIAL', 'OTHER']
const PROCESS_TYPES = ['CARDED', 'COMBED', 'COMPACT', 'OPEN_END', 'AIRJET', 'ROTOR']

export default function YarnRecipesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<YarnRecipe | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | undefined>()

  const canCreate = usePermission('recipe.create')
  const canUpdate = usePermission('recipe.update')
  const canApprove = usePermission('recipe.approve')

  const { data: recipes = [], isLoading } = useQuery({
    queryKey: ['yarn-recipes', plantId, statusFilter],
    queryFn: () => recipeApi.list({ plant_id: plantId, status: statusFilter }),
    enabled: !!plantId,
  })

  const saveMutation = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      editing ? recipeApi.update(editing.id, values)
              : recipeApi.create({ ...values, plant_id: plantId }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['yarn-recipes'] })
      message.success(editing ? 'Recipe updated' : `Recipe ${r.recipe_code} created`)
      setDrawerOpen(false)
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Save failed'),
  })

  const submitMutation = useMutation({
    mutationFn: (id: string) => recipeApi.submit(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['yarn-recipes'] })
      message.success('Submitted for approval')
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  const approveMutation = useMutation({
    mutationFn: (data: { id: string; status: 'APPROVED' | 'REJECTED'; remarks?: string }) =>
      recipeApi.approve(data.id, { status: data.status, remarks: data.remarks }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['yarn-recipes'] })
      message.success(r.status === 'ACTIVE' ? 'Recipe activated' : 'Recipe rejected')
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Failed'),
  })

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      end_use: 'KNITTING',
      process_type: 'CARDED',
      max_lot_pct: 35,
      min_lot_pct: 10,
      max_laydown_bales: 60,
      max_soft_waste_pct: 3.0,
      // Sprint 0068 — hard diversity rules
      max_lots_per_supplier: 3,
      max_bales_per_lot: 5,
    })
    setDrawerOpen(true)
  }

  const openEdit = (r: YarnRecipe) => {
    setEditing(r)
    form.setFieldsValue(r)
    setDrawerOpen(true)
  }

  const onSubmit = () => form.validateFields().then(v => saveMutation.mutate(v))

  const columns = [
    { title: 'Code', dataIndex: 'recipe_code', width: 130,
      render: (v: string) => <Text strong style={{ fontFamily: 'monospace' }}>{v}</Text> },
    { title: 'Name', dataIndex: 'recipe_name' },
    {
      title: 'Yarn', key: 'yarn',
      render: (_: unknown, r: YarnRecipe) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">Ne {r.count_ne}</Tag>
          <Text style={{ fontSize: 11 }}>{r.process_type} · {r.end_use}</Text>
        </Space>
      ),
    },
    {
      title: 'Targets', key: 'targets',
      render: (_: unknown, r: YarnRecipe) => (
        <Space direction="vertical" size={2} style={{ fontSize: 11 }}>
          <span>Mic: {r.target_mic ?? '—'} ({r.target_mic_min ?? '?'}–{r.target_mic_max ?? '?'})</span>
          <span>UHML: {r.target_uhml_mm ?? '—'}mm (≥{r.target_uhml_min ?? '?'})</span>
          <span>Str: {r.target_strength ?? '—'} g/tex</span>
          <span>Trash: ≤{r.target_trash_max ?? '—'}%</span>
        </Space>
      ),
    },
    {
      title: 'Mixing', key: 'mixing',
      render: (_: unknown, r: YarnRecipe) => (
        <Space direction="vertical" size={2} style={{ fontSize: 11 }}>
          <span>Bales: ≤{r.max_laydown_bales}</span>
          <span>Lot: {r.min_lot_pct}–{r.max_lot_pct}%</span>
          <span>Soft Waste: ≤{r.max_soft_waste_pct}%</span>
          <span>Cost: ≤{r.max_cost_per_kg ?? '—'} ₹/kg</span>
          {/* Sprint 0068 — diversity rules (hard) */}
          <span style={{ color: '#1677ff' }}>
            ≤{r.max_lots_per_supplier ?? 3} lots/supplier · ≤{r.max_bales_per_lot ?? 5} bales/lot
          </span>
        </Space>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 120,
      render: (v: string) => <Tag color={STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 320,
      render: (_: unknown, r: YarnRecipe) => (
        <Space size={4} wrap>
          {canUpdate && ['DRAFT', 'SUBMITTED'].includes(r.status) && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
          )}
          {canUpdate && r.status === 'DRAFT' && (
            <Popconfirm title="Submit for approval?" onConfirm={() => submitMutation.mutate(r.id)}>
              <Button size="small" type="primary" ghost icon={<SendOutlined />}>Submit</Button>
            </Popconfirm>
          )}
          {canApprove && r.status === 'SUBMITTED' && (
            <>
              <Popconfirm
                title="Approve & Activate?"
                description="This will supersede any existing ACTIVE recipe for the same yarn count + end use + process."
                onConfirm={() => approveMutation.mutate({ id: r.id, status: 'APPROVED' })}>
                <Button size="small" type="primary" icon={<CheckOutlined />}>Approve</Button>
              </Popconfirm>
              <Button
                size="small" danger icon={<CloseOutlined />}
                onClick={() => {
                  let reason = ''
                  Modal.confirm({
                    title: 'Reject Recipe',
                    content: (
                      <Input.TextArea
                        rows={3} placeholder="Reason for rejection"
                        onChange={(e) => { reason = e.target.value }}
                      />
                    ),
                    onOk: () => approveMutation.mutate({ id: r.id, status: 'REJECTED', remarks: reason }),
                  })
                }}>
                Reject
              </Button>
            </>
          )}
          {canUpdate && r.status === 'ACTIVE' && (
            <Tag color="green" icon={<CheckOutlined />}>Active</Tag>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Yarn Quality Targets"
        subtitle="Target Mic / UHML / Strength per yarn count + end use + process. The HVI bullseye each laydown is aimed at."
        extra={canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Recipe</Button>
        )}
      />

      <Space style={{ marginBottom: 16 }}>
        <Select
          placeholder="Filter by status" allowClear style={{ width: 200 }}
          value={statusFilter} onChange={setStatusFilter}
          options={['DRAFT', 'SUBMITTED', 'ACTIVE', 'SUPERSEDED', 'ARCHIVED'].map(s => ({ value: s, label: s }))}
        />
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={recipes}
        loading={isLoading}
        size="middle"
      />

      <FormDrawer
        title={editing ? `Edit Recipe: ${editing.recipe_code}` : 'New Yarn Recipe'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmit}
        loading={saveMutation.isPending}
        form={form}
        width={760}
      >
        <Alert
          message="Recipe defines target HVI properties. Auto-Assignment Engine selects bales matching these targets."
          type="info" showIcon style={{ marginBottom: 12 }}
        />

        <Tabs
          items={[
            {
              key: 'basic',
              label: 'Yarn Definition',
              children: (
                <>
                  <Form.Item name="recipe_name" label="Recipe Name" rules={[{ required: true }]}>
                    <Input placeholder="e.g. Ne 30 Carded Knitting Premium" />
                  </Form.Item>
                  <Row gutter={12}>
                    <Col span={8}>
                      <Form.Item name="count_ne" label="Count (Ne)" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} min={1} step={0.5} placeholder="30" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="end_use" label="End Use" rules={[{ required: true }]}>
                        <Select options={END_USES.map(v => ({ value: v, label: v }))} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="process_type" label="Process" rules={[{ required: true }]}>
                        <Select options={PROCESS_TYPES.map(v => ({ value: v, label: v.replace('_', ' ') }))} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="yarn_description" label="Description">
                    <Input placeholder="e.g. for premium knit fabric, 100% cotton" />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'targets',
              label: 'Target HVI',
              children: (
                <>
                  <Divider orientation="left" plain>Micronaire</Divider>
                  <Row gutter={12}>
                    <Col span={8}><Form.Item name="target_mic" label="Target"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="target_mic_min" label="Min"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="target_mic_max" label="Max"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
                  </Row>

                  <Divider orientation="left" plain>UHML (Length)</Divider>
                  <Row gutter={12}>
                    <Col span={8}><Form.Item name="target_uhml_mm" label="Target (mm)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="target_uhml_min" label="Min"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="target_uhml_max" label="Max"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                  </Row>

                  <Divider orientation="left" plain>Strength + Uniformity</Divider>
                  <Row gutter={12}>
                    <Col span={6}><Form.Item name="target_strength" label="Strength (g/tex)"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    <Col span={6}><Form.Item name="target_strength_min" label="Min Str"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    <Col span={6}><Form.Item name="target_uniformity" label="Uniformity %"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    <Col span={6}><Form.Item name="target_uniformity_min" label="Min UI"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                  </Row>

                  <Divider orientation="left" plain>Trash + SFC limits</Divider>
                  <Row gutter={12}>
                    <Col span={12}><Form.Item name="target_trash_max" label="Trash Max %"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    <Col span={12}><Form.Item name="target_sfc_max" label="SFC/SFI Max %"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                  </Row>

                  <Divider orientation="left" plain>Color (Rd / +b)</Divider>
                  <Row gutter={12}>
                    <Col span={8}><Form.Item name="target_rd" label="Rd target"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="target_rd_min" label="Rd min"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="target_rd_max" label="Rd max"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                  </Row>
                  <Row gutter={12}>
                    <Col span={8}><Form.Item name="target_plus_b" label="+b target"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="target_plus_b_min" label="+b min"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                    <Col span={8}><Form.Item name="target_plus_b_max" label="+b max"><InputNumber style={{ width: '100%' }} step={0.1} /></Form.Item></Col>
                  </Row>
                </>
              ),
            },
            {
              key: 'mixing',
              label: 'Mixing Constraints',
              children: (
                <>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item name="max_laydown_bales" label="Max Bales per Laydown" rules={[{ required: true }]}>
                        <InputNumber style={{ width: '100%' }} min={10} max={200} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="max_cost_per_kg" label="Max Cost ₹/kg">
                        <InputNumber style={{ width: '100%' }} step={0.5} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col span={8}>
                      <Form.Item name="min_lot_pct" label="Min Lot %"
                        help="Each lot at least this % of laydown">
                        <InputNumber style={{ width: '100%' }} min={0} max={100} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="max_lot_pct" label="Max Lot %"
                        help="No single lot more than this %">
                        <InputNumber style={{ width: '100%' }} min={0} max={100} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item name="max_soft_waste_pct" label="Max Soft Waste %"
                        help="Recycled soft waste cap">
                        <InputNumber style={{ width: '100%' }} min={0} max={10} step={0.1} />
                      </Form.Item>
                    </Col>
                  </Row>
                  {/* Sprint 0068 — hard diversity rules. These are
                      ENFORCED during picking (not advisory like the
                      Uster tolerances). Defaults match standard mill
                      practice; tighten for fine counts if needed. */}
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Diversity Rules — enforced by the algorithm"
                    description="These are HARD caps the bale-selection algorithm respects. Bales that would violate them are dropped from the candidate pool before picking. Different from the Uster tolerances (Mic CV, drift) which are reference bands only."
                  />
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item name="max_lots_per_supplier"
                        label="Max Lots per Supplier"
                        help="Cannot mix bales from more than this many distinct lots of one supplier (default 3)"
                        rules={[{ required: true, type: 'number', min: 1, max: 20 }]}>
                        <InputNumber style={{ width: '100%' }} min={1} max={20} step={1} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="max_bales_per_lot"
                        label="Max Bales per Lot"
                        help="Cannot pick more than this many bales from any single lot (default 5)"
                        rules={[{ required: true, type: 'number', min: 1, max: 100 }]}>
                        <InputNumber style={{ width: '100%' }} min={1} max={100} step={1} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item name="notes" label="Notes">
                    <Input.TextArea rows={3} />
                  </Form.Item>
                </>
              ),
            },
          ]}
        />
      </FormDrawer>
    </div>
  )
}
